// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { Flex, Pagination, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

// -----------------------------------------------------------------------------
// Components, helpers, styles and selectors
// -----------------------------------------------------------------------------
import StyledWorkoutsList from "./styles";
import { decodeString } from "utils";
import { WorkoutCard } from "components/Workouts/WorkoutCard";
import { selectMembersWorkoutsSearchTerm, selectMembersWorkoutsSelectedCategories } from "store/selectors";
import { useGetDomainGymQuery, useLazyGetPaginatedWorkoutsQuery } from "store/api/api";
import { workoutStatuses, workoutTypes } from "helpers/constants";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const WorkoutsList = ({ dateRange }) => {
	const navigate = useNavigate();
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const [fetchPaginatedWorkouts, paginatedWorkouts] = useLazyGetPaginatedWorkoutsQuery();
	const domainGymId = domainGymResponse?.data?.id;
	const primaryColor = usePrimaryColor();
	const categoriesFilters = useSelector(selectMembersWorkoutsSelectedCategories);
	const searchTerm = useSelector(selectMembersWorkoutsSearchTerm);
	const [filters, setFilters] = useState({
		page: 1,
		pageSize: 20,
		statuses: [workoutStatuses.ACTIVE],
		types: [workoutTypes.ON_DEMAND],
		orderBy: "created_date",
		order: "desc",
	});

	useEffect(() => {
		if (domainGymId) {
			const categories = categoriesFilters.filter(Boolean);
			fetchPaginatedWorkouts({
				gymId: domainGymId,
				search: searchTerm,
				startDate: dateRange.startDate ? dateRange?.startDate?.format("YYYY-MM-DD") : '',
				endDate: dateRange.endDate ? dateRange?.endDate?.format("YYYY-MM-DD") : '',
				categories,
				...filters,
			});
		}
	}, [
		filters,
		domainGymId,
		fetchPaginatedWorkouts,
		searchTerm,
		dateRange?.startDate,
		dateRange?.endDate,
		categoriesFilters,
	]);

	const handleFileSelected = (workoutId) => {
		navigate(`../view-workout/${workoutId}`);
	};

	const filteredWorkouts = paginatedWorkouts?.data?.workouts;

	if (!filteredWorkouts || !filteredWorkouts.length) {
		return (
			<Flex className="empty-state" justify="center" align="center" gap={8}>
				<InfoCircleOutlined style={{ color: primaryColor }} />
				<Typography.Text style={{ color: "#000", fontSize: 16, fontWeight: 500 }}>
					No Search Results Available
				</Typography.Text>
			</Flex>
		);
	}

	return (
		<>
			<StyledWorkoutsList className="sb-workouts">
				{filteredWorkouts
					.slice()
					.sort((a, b) => new Date(b.created_date) - new Date(a.created_date))
					.map((workout) => (
						<div key={workout.id} onClick={() => handleFileSelected(workout.id)}>
							<WorkoutCard
								title={decodeString(workout.name)}
								categories={workout.categories}
								headerImage={workout.image_url}
							/>
						</div>
					))}
			</StyledWorkoutsList>
			<Flex justify="flex-end" align="center" gap={8} style={{ marginTop: 16 }}>
				<Pagination
					onChange={(page, pageSize) => {
						setFilters((prev) => ({
							...prev,
							page,
							pageSize,
						}));
					}}
					current={paginatedWorkouts?.data?.meta?.current_page}
					pageSize={paginatedWorkouts?.data?.meta?.page_size || 10}
					total={paginatedWorkouts?.data?.meta?.total_items}
				/>
			</Flex>
		</>
	);
};

export default WorkoutsList;
